import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import React from 'react'

import ActionsBox from '@/app/module/campaigns/component/actions/actions-box'
import { LocalAction, LocalActionType, SenderIdOption } from '@/app/module/campaigns/types'
import { Action } from '@/app/module/campaigns/types/schemas/actions/all-actions'
import { addDescriptionToActions, getActionOptions } from '@/app/module/campaigns/utils/actions/options'
import { ActionContext } from '@/app/module/campaigns/utils/actions/types'
import { unwrapActions, wrapActions } from '@/app/module/campaigns/utils/actions/wrap'
import { getInboundPersonalization, normalizePickupActions } from '@/app/module/phonenumbers/helpers'
import {
  ChannelType,
  InboundChannelType,
  PhoneNumber,
  PhoneNumberGetFile,
  PhoneNumberSaveFile,
} from '@/app/module/phonenumbers/types'
import {
  CountryTimezone,
  FileType,
  FileUploadType,
  PersonalizationType,
  TimezoneWithCountryCodeType,
} from '@/app/types'

type Props = {
  countriesTimezones: CountryTimezone[]
  countryCode: string
  files: { data: Record<string, FileType>; loadingItems: string[] }
  item: { actions: Action[] }
  orgId: number
  personalizationList: PersonalizationType
  phoneNumber: PhoneNumber
  senderIds: SenderIdOption[]
  timezone: string
  timezones: TimezoneWithCountryCodeType
  title: string
  token: string
  type: InboundChannelType

  changeHandler: (actionsList: Action[]) => void
  getFile: PhoneNumberGetFile
  onMicAccessError: () => void
  saveFile: PhoneNumberSaveFile
}

const HookAction: React.FC<Props> = ({
  countriesTimezones,
  countryCode,
  item: { actions: itemActions },
  files,
  orgId,
  personalizationList,
  phoneNumber,
  senderIds,
  timezone,
  timezones,
  title,
  token,
  type,
  changeHandler,
  getFile,
  onMicAccessError,
  saveFile,
}) => {
  const [voicePlayingId, setVoicePlayingId] = React.useState<string>()

  const actions = React.useMemo(() => {
    const unwrapped = unwrapActions(itemActions)
    return type === ChannelType.Voice ? normalizePickupActions(unwrapped) : unwrapped
  }, [itemActions, type])
  const actionOptions = React.useMemo(() => addDescriptionToActions(actionValues(type), ActionContext.Inbound), [type])

  const pzList = React.useMemo(() => getInboundPersonalization(type, personalizationList), [personalizationList, type])

  const getFileHandler = React.useCallback(
    (msgId: string, file: string) => getFile({ token, orgId, msgId, file }),
    [orgId, token, getFile],
  )

  const saveFileHandler = React.useCallback(
    (msgId: string, payload: FileUploadType) => saveFile({ token, orgId, msgId, item: payload }),
    [orgId, token, saveFile],
  )

  const getImageProps = (qid: string) => ({
    id: qid,
    loading: files.loadingItems.indexOf(qid) > -1,
    getFile: (file: string) => getFileHandler(qid, file),
    saveFileHandler: (item: FileUploadType) => saveFileHandler(qid, item),
  })

  const getVoiceProps = React.useCallback(
    (qid: string) => ({
      id: qid,
      files: files.data,
      filterType: 'whatsapp',
      loading: files.loadingItems.indexOf(qid) > -1,
      playId: voicePlayingId,
      personalizationList: pzList,
      getFile: (file: string) => getFileHandler(qid, file),
      onMicAccessError,
      saveFileHandler: (item: FileUploadType) => saveFileHandler(qid, item),
      setPlaying: (val?: string) => setVoicePlayingId(val),
    }),
    [files, pzList, voicePlayingId, getFileHandler, onMicAccessError, saveFileHandler],
  )

  const handleActionsChange = React.useCallback(
    (newActions: LocalAction[]) => {
      changeHandler(wrapActions(newActions))
    },
    [changeHandler],
  )

  return (
    <div>
      <Divider style={{ margin: '0rem -16px 1rem' }} />
      <div>
        <Typography variant="caption" style={{ color: 'rgba(0, 0, 0, 0.54)' }}>
          {title}
        </Typography>
        <div>
          <ActionsBox
            actionOptions={actionOptions}
            actions={actions}
            continueLabel="Continue to next rule"
            countriesTimezones={countriesTimezones}
            countryCode={countryCode}
            defaultLanguage=""
            files={files.data}
            hasTranscribe={false}
            hideFinalActions={type !== ChannelType.Voice}
            index={0}
            info={
              type === ChannelType.Voice
                ? 'The first pickup wins. All actions in this rule finish before pickup, and later rules are skipped.'
                : undefined
            }
            languages={[]}
            orgId={orgId}
            phoneNumber={phoneNumber}
            personalizationList={pzList}
            senderIds={senderIds}
            timezones={timezones}
            timezone={timezone}
            token={token}
            transcribeEnabled={false}
            updateContactConfig={updateContactConfig}
            voiceId={`inbound-${type}`}
            whatsappSenderIds={[]}
            getImageProps={getImageProps}
            getVoiceProps={getVoiceProps}
            onChange={handleActionsChange}
          />
        </div>
      </div>
    </div>
  )
}

const actionValues = (type: string) =>
  getActionOptions([
    ...(type === 'whatsapp' ? [LocalActionType.SendWhatsApp] : []),
    LocalActionType.SubscribeToCampaign,
    LocalActionType.UpdateContact,
    LocalActionType.SendSMS,
    LocalActionType.SendSMSToPhoneNumbers,
    LocalActionType.SendEmail,
    LocalActionType.API,
    LocalActionType.UnsubscribeAll,
    ...(type === ChannelType.Voice ? [LocalActionType.Pickup] : []),
  ]).map((option) => ({ ...option, final: option.value === LocalActionType.Pickup }))

const updateContactConfig = {
  invalid: false,
  question: false,
  openResponse: false,
}

export default HookAction
